import { useMemo } from 'react';

import { useRouter } from 'next/router';
import { useAccount } from 'wagmi';

import MoreIcon from '@/assets/icons/elipses.svg';
import NumberWithTooltip from '@/components/shared/tooltips/NumberTooltip';
// import { useMakeCounterOffer } from '@/hooks/loans/useMakeCounterOffer';
// import { useSelectNFT } from '@/hooks/loans/useSelectNft';
import { useDisplayLTV } from '@/hooks/loans/useDisplayLTV';
import { useAppSelector } from '@/hooks/redux';
import { useUserBorrower } from '@/hooks/useUserBorrower';
import { displayRepay } from '@/utils/formatter';
import { TLoanTableHeaders } from 'types/loan';

// import LoanFormModal from './LoanFormModal';
import BestOfferColumn from './BestOfferColumn';
import LoanGridItemView from './LoanGridItemView';
import NettyScore from './NettyScore';
import CryptoHeadButton from '../crypto-prices/CryptoHeadButton';
import TableUi from '../top-sales/TableUi';

const cryptoColumns: TLoanTableHeaders = [
  {
    Header: (props) => <CryptoHeadButton className='pr-0' columnName='Asset' {...props} />,
    accessor: 'tokenId',
    disableSortBy: true,
  },
  {
    Header: (props) => <CryptoHeadButton className='!pr-0' columnName='Repayment Rate' {...props} />,
    accessor: 'nettyScore',
    disableSortBy: true,
    sortId: 'user.nettyScore',
    thPClassName: 'mx-auto',
    thClassName: 'w-44 px-2',
  },

  {
    Header: (props) => <CryptoHeadButton className='!pr-0' columnName='Requests' {...props} />,
    accessor: 'principal',
    disableSortBy: true,
    thPClassName: 'mx-auto',
    thClassName: 'w-28 px-2',
  },
  {
    Header: (props) => <CryptoHeadButton className='!pr-0' columnName='APR' {...props} />,
    accessor: 'apr',
    disableSortBy: true,
    thPClassName: 'mx-auto',
    thClassName: 'w-24',
  },
  {
    Header: (props) => <CryptoHeadButton className='!pr-0' columnName='LTV' {...props} />,
    accessor: 'ltv',
    disableSortBy: true,
    thPClassName: 'mx-auto',
    thClassName: 'w-24 px-2',
  },
  {
    Header: (props) => <CryptoHeadButton className='!pr-0' columnName='Repay' {...props} />,
    accessor: 'repay',
    disableSortBy: true,
    thPClassName: 'mx-auto',
    thClassName: 'w-28 px-2',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='Duration' {...props} />,
    accessor: 'term',
    disableSortBy: true,
    thPClassName: 'mx-auto',
    thClassName: 'w-28 px-2',
  },
  {
    Header: (props) => <CryptoHeadButton className='!pr-0' columnName='Floor' {...props} />,
    accessor: 'floor',
    disableSortBy: true,
    thPClassName: 'mx-auto',
    thClassName: 'w-24 px-2',
  },
  {
    Header: (props) => <CryptoHeadButton columnName='OS Buy Offer' disableSorting {...props} />,
    accessor: 'bestOffer',
    disableSortBy: true,
    thPClassName: 'mx-auto',
    thClassName: 'w-28 px-7',
  },
  {
    accessor: 'action',
    thPClassName: 'mx-auto',
    thClassName: 'w-32 px-2',
  },
  {
    accessor: 'more',
    thPClassName: 'mx-auto',
    thClassName: 'w-8',
  },
];

const LendLoan = () => {
  const router = useRouter();
  const { lendingOffers, loanCardView } = useAppSelector((state) => state.loans);

  const displayLtv = useDisplayLTV(false);
  const { address } = useAccount();

  // const { NFT, setNFT } = useSelectNFT();
  // const { makeCounterOffer: makeCounterOfferOnChain } = useMakeCounterOffer();

  const { checkIsBorrower } = useUserBorrower(false, true);

  // const chooseNft = useCallback(
  //   (e: MouseEvent<HTMLButtonElement>, { loanTokenOwner, tokenId, convertedFloorPrice }: ILoanRequest & { convertedFloorPrice: number | null }) => {
  //     e.stopPropagation();
  //     setNFT({
  //       collectionName: loanTokenOwner.collectionName,
  //       floorPrice: convertedFloorPrice,
  //       imageUrl: loanTokenOwner.image,
  //       tokenId,
  //       collectionAddress: loanTokenOwner.collectionAddress,
  //       ownerAddress: loanTokenOwner.ownerAddress,
  //     });
  //   },
  //   [setNFT]
  // );

  const displayData = useMemo(() => {
    return lendingOffers.map((nft) => {
      const {
        loanTokenOwner,
        user: { id: userId },
      } = nft;
      const convertedFloorPrice = loanTokenOwner.floorPrice || null;

      const isSenderBorrower = checkIsBorrower({
        ownerAddress: loanTokenOwner.ownerAddress,
        senderId: userId,
        lenderAddress: undefined,
      });

      return {
        ...nft,
        hideLendButton: !address || isSenderBorrower || isSenderBorrower === null,
        convertedFloorPrice,
      };
    });
  }, [checkIsBorrower, lendingOffers, address]);

  const tableData = useMemo(() => {
    const filterData = displayData.map((nft) => {
      const {
        repay,
        term,
        tokenId,
        loanTokenOwner,
        apr,
        principal,
        user: { nettyScore },
        token,
        convertedFloorPrice,
        hideLendButton,
      } = nft;
      const {
        collectionToken: { slug },
        name,
      } = loanTokenOwner;

      return {
        tokenId: (
          <div className='flex gap-[14px] items-center pl-6 w-max relative pr-1'>
            {/* {tableData.length > 1 && <span className='absolute size-[18px] rounded-full flex items-center justify-center bg-primary left-4'>{tableData.length}</span>} */}
            <img src={loanTokenOwner.image || '/bayc-collectors.png'} className='size-9 rounded object-cover' alt='loan image' />
            <div className='flex flex-col'>
              <p className='w-max font-medium text-base'>{loanTokenOwner.collectionName}</p>
              <p className='w-max font-medium text-xs'># {name || tokenId}</p>
            </div>
          </div>
        ),
        bestOffer: <BestOfferColumn collectionSlug={slug} />,
        principal: (
          <NumberWithTooltip fullNumber={principal} symbol={token?.symbol} wrapperClassName='w-full'>
            <p className='text-sm font-normal font-space-mono w-max mx-auto'>
              {displayRepay(principal)} {token?.symbol}
            </p>
          </NumberWithTooltip>
        ),
        apr: (
          <NumberWithTooltip fullNumber={apr} symbol={'%'} wrapperClassName='w-full'>
            <p className='text-sm font-normal w-max font-space-mono mx-auto'>{displayRepay(apr)}%</p>
          </NumberWithTooltip>
        ),
        ltv: (
          <NumberWithTooltip fullNumber={displayLtv(principal, loanTokenOwner.floorPrice, token?.symbol)} symbol={''} wrapperClassName='w-full'>
            <p className='text-sm font-normal w-max font-space-mono mx-auto'>{displayLtv(principal, loanTokenOwner.floorPrice, token?.symbol)}</p>
          </NumberWithTooltip>
        ),
        repay: (
          <NumberWithTooltip fullNumber={repay} symbol={token?.symbol} wrapperClassName='w-full'>
            <p className='text-sm font-normal font-space-mono w-max mx-auto'>
              {displayRepay(repay)} {token?.symbol}
            </p>
          </NumberWithTooltip>
        ),
        term: <p className='text-sm font-normal w-max font-space-mono mx-auto'>{term} Days</p>,
        floor: (
          <NumberWithTooltip fullNumber={convertedFloorPrice || 'N/A'} symbol={convertedFloorPrice ? 'WETH' : ''} wrapperClassName='w-full'>
            <p className='text-sm font-normal w-max font-space-mono mx-auto'>{convertedFloorPrice ? `${displayRepay(convertedFloorPrice)} WETH` : 'N/A'}</p>
          </NumberWithTooltip>
        ),
        more: (
          <button className='w-[3px] h-3 mr-4 !bg-transparent'>
            <MoreIcon className='[&_path]:fill-coal-250' />
          </button>
        ),
        action: hideLendButton ? null : (
          <button
            // onClick={(e) => chooseNft(e, { ...nft, convertedFloorPrice })}
            className='w-max mx-auto bg-primary rounded text-white text-xs font-semibold py-3 px-[22px]'
          >
            Make Offer
          </button>
        ),
        collection_id: tokenId,
        nettyScore: <NettyScore nettyScore={nettyScore} width={38} height={38} outerRadius={19} innerRadius={16} className='mx-auto' />,
      };
    });

    return filterData;
  }, [displayData, displayLtv]);

  const handleNavigate = (id: number) => router.push(`/loans/${id}/offers`);

  // const makeCounterOffer: SubmitHandler<IDesiredTerms> = async (data) => {
  //   if (!NFT) return;

  //   await makeCounterOfferOnChain(data, {
  //     collectionAddress: NFT.collectionAddress,
  //     ownerAddress: NFT.ownerAddress,
  //     tokenId: Number(NFT.tokenId),
  //   });
  //   setNFT(null);
  // };

  return (
    <>
      <div className='overflow-x-auto h-full hidden lg:block custom-scrollbar'>
        {loanCardView === 'card' ? (
          <div className='grid [grid-template-columns:repeat(auto-fill,minmax(236px,1fr))] pb-20'>
            {displayData.map((nft) => {
              const {
                id,
                tokenId,
                principal,
                loanTokenOwner: { collectionName, image, floorPrice },
                user: { nettyScore },
                repay,
                term,
                token,
                apr,
                convertedFloorPrice,
                hideLendButton,
              } = nft;

              const loanDetails = [
                { label: 'LTV', value: displayLtv(principal, floorPrice, token?.symbol), className: 'items-center' },
                { label: 'Repay', value: displayRepay(repay), symbol: token?.symbol, className: 'items-center' },
                { label: 'Duration', value: `${term} days` },
              ];
              const loanInfo = [
                { label: 'Floor', value: convertedFloorPrice ? `${convertedFloorPrice} ETH` : 'N/A' },
                { label: 'Principal', value: principal, symbol: token?.symbol, className: 'items-center' },
                { label: 'APR', value: `${apr}`, symbol: '%', className: 'items-end' },
              ];

              return (
                <LoanGridItemView
                  onClick={() => router.push(`/loans/${tokenId}/offers`)}
                  hideButton={hideLendButton}
                  tag={`${nettyScore || 0}% Repayment Rate`}
                  btnText='Make Offer'
                  key={id}
                  // handleAction={(e) => chooseNft(e, { ...nft, convertedFloorPrice })}
                  name={collectionName}
                  id={tokenId}
                  loanDetails={loanDetails}
                  loanInfo={loanInfo}
                  image={image}
                />
              );
            })}
          </div>
        ) : (
          <TableUi wrapperStyle='pb-20' enableDefaultSort handleNavigateDetails={(id) => handleNavigate(id)} columns={cryptoColumns} data={tableData} loading={false} hoverClass='dark:hover:!bg-jet-black-100 hover:!bg-light-blue-grey border-[2px] h-20' />
        )}
      </div>
      {/* <div className='flex flex-col mt-[14px] pb-20 lg:hidden'>
        {displayData.map(({ id, floorPrice, repay, term, tokenId, loanTokenOwner, apr, principal, user: { nettyScore }, token, convertedFloorPrice }) => {
          const info = [
            {
              title: 'Floor',
              value: convertedFloorPrice ? `${convertedFloorPrice} ETH` : 'N/A',
            },
            {
              title: 'Loan',
              value: `${principal} ${token?.symbol}`,
            },
            {
              title: 'APR',
              value: `${apr}%`,
            },
            {
              title: 'LTV',
              value: displayLtv(principal, floorPrice || 0, token?.symbol),
            },
            {
              title: 'Repay',
              value: `${Number(displayRepay(repay).toFixed(3))} ${token?.symbol}`,
            },
            {
              title: 'Duration',
              value: `${term} Days`,
            },
          ];
          return (
            <div key={id} onClick={() => handleNavigate(tokenId)}>
              <LoanItem name={tokenId.toString()} imageUrl={loanTokenOwner.image} nettyScore={nettyScore} collectionName={loanTokenOwner.collectionName} id={tokenId} uniqueId={tokenId} details={info} />
            </div>
          );
        })}
      </div> */}
      {/* {NFT && <LoanFormModal handleClose={() => setNFT(null)} {...NFT} onSubmit={makeCounterOffer} />} */}
    </>
  );
};

export default LendLoan;
